import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../components/ui/table';
import { toast } from 'sonner';
import { outreachQueueApi } from '../lib/api';
import { Inbox, CheckCircle, XCircle, Pencil, Save, X, Loader2, AlertTriangle, Send, SendHorizonal, RotateCw, Eye, Unlock } from 'lucide-react';

const friendlyError = (err) => {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    if (detail.reason === 'url_allowlist_violation') {
      const hosts = (detail.disallowed_hosts || []).join(', ');
      return `Can't approve — this message links to a website that isn't on the approved list (${hosts}). Edit the message to remove or fix the link, then try again.`;
    }
    return detail.reason ? `Blocked: ${detail.reason}` : 'This action was blocked by a safety check.';
  }
  if (typeof detail === 'string' && detail) return detail;
  return 'Something went wrong. Please try again.';
};

const OutreachQueue = () => {
  const [items, setItems] = useState([]);
  const [failedItems, setFailedItems] = useState([]);
  const [heldItems, setHeldItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [sendingAll, setSendingAll] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ subject: '', body: '' });
  const [rowMessages, setRowMessages] = useState({});

  const fetchQueue = useCallback(async () => {
    try {
      const { data } = await outreachQueueApi.list('pulse');
      setItems(data.items || []);
    } catch (err) {
      toast.error('Failed to load the approval queue');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFailed = useCallback(async () => {
    try {
      const { data } = await outreachQueueApi.listFailed('pulse');
      setFailedItems(data.items || []);
    } catch (err) {
      // non-fatal -- failed/retry panel just stays empty
    }
  }, []);

  const fetchHeld = useCallback(async () => {
    try {
      const { data } = await outreachQueueApi.listHeld('pulse');
      setHeldItems(data.items || []);
    } catch (err) {
      // non-fatal -- held panel just stays empty
    }
  }, []);

  useEffect(() => { fetchQueue(); fetchFailed(); fetchHeld(); }, [fetchQueue, fetchFailed, fetchHeld]);

  const handleRelease = async (item) => {
    setProcessing((prev) => ({ ...prev, [`release-${item.id}`]: true }));
    try {
      await outreachQueueApi.releaseHold(item.outreach_id);
      toast.success('Released — now back in the active queue');
      fetchQueue();
      fetchHeld();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setProcessing((prev) => ({ ...prev, [`release-${item.id}`]: false }));
    }
  };

  const handleSendAll = async () => {
    const draftCount = items.filter((i) => i.status !== 'approved').length;
    const approvedCount = items.length - draftCount;
    const confirmed = window.confirm(
      `This will approve ${draftCount} drafted message(s) and send all ${items.length} message(s) in the queue ` +
      `(${draftCount} drafted + ${approvedCount} already approved) via Resend, right now. This cannot be undone. Continue?`
    );
    if (!confirmed) return;
    setSendingAll(true);
    try {
      const { data } = await outreachQueueApi.sendAll('pulse');
      toast.success(`Sent ${data.sent} of ${data.total}`);
      if (data.failed_or_blocked > 0) {
        toast.warning(`${data.failed_or_blocked} item(s) failed or were blocked — check the Failed/Retry panel`);
      }
      fetchQueue();
      fetchFailed();
    } catch (err) {
      toast.error('Send All failed to run — see console for details');
      console.error(err);
    } finally {
      setSendingAll(false);
    }
  };

  const handleRetry = async (item) => {
    setProcessing((prev) => ({ ...prev, [`retry-${item.id}`]: true }));
    try {
      await outreachQueueApi.send(item.outreach_id);
      toast.success('Retry sent');
      fetchQueue();
      fetchFailed();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setProcessing((prev) => ({ ...prev, [`retry-${item.id}`]: false }));
    }
  };

  const setRowMessage = (id, msg) => setRowMessages((prev) => ({ ...prev, [id]: msg }));

  const handleApprove = async (item) => {
    setProcessing((prev) => ({ ...prev, [item.id]: true }));
    setRowMessage(item.id, null);
    try {
      await outreachQueueApi.approve(item.outreach_id);
      toast.success('Message approved');
      fetchQueue();
    } catch (err) {
      setRowMessage(item.id, friendlyError(err));
    } finally {
      setProcessing((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const handleSend = async (item) => {
    setProcessing((prev) => ({ ...prev, [item.id]: true }));
    setRowMessage(item.id, null);
    try {
      await outreachQueueApi.send(item.outreach_id);
      toast.success('Email sent');
      fetchQueue();
    } catch (err) {
      setRowMessage(item.id, friendlyError(err));
    } finally {
      setProcessing((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const handleReject = async (item) => {
    setProcessing((prev) => ({ ...prev, [item.id]: true }));
    setRowMessage(item.id, null);
    try {
      await outreachQueueApi.reject(item.outreach_id);
      toast.success('Message rejected');
      fetchQueue();
    } catch (err) {
      setRowMessage(item.id, friendlyError(err));
    } finally {
      setProcessing((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setDraft({ subject: item.subject || '', body: item.body || '' });
    setRowMessage(item.id, null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({ subject: '', body: '' });
  };

  const saveEdit = async (item) => {
    setProcessing((prev) => ({ ...prev, [item.id]: true }));
    try {
      await outreachQueueApi.update(item.outreach_id, { subject: draft.subject, body: draft.body });
      toast.success('Message updated');
      setEditingId(null);
      fetchQueue();
    } catch (err) {
      setRowMessage(item.id, friendlyError(err));
    } finally {
      setProcessing((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  return (
    <div data-testid="outreach-queue-page">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Approval Queue</h1>
          <p className="text-muted-foreground">
            Review drafted messages before they go out. Nothing is sent until you approve it here.
          </p>
        </div>
        {items.length > 0 && (
          <Button onClick={handleSendAll} disabled={sendingAll} data-testid="send-all-button">
            {sendingAll ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <SendHorizonal className="w-4 h-4 mr-1" />}
            Approve &amp; Send All ({items.length})
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <Card className="bg-card border-border card-glow">
          <CardContent className="py-16 text-center">
            <Inbox className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold mb-2">No drafts awaiting approval.</h3>
            <p className="text-muted-foreground">New messages will show up here once they're drafted.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-card border-border card-glow">
          <CardHeader>
            <CardTitle>Drafted messages ({items.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Touch #</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => {
                  const isEditing = editingId === item.id;
                  const isProcessing = !!processing[item.id];
                  return (
                    <React.Fragment key={item.id}>
                      <TableRow data-testid={`queue-row-${item.id}`}>
                        <TableCell className="font-medium">{item.firm_name || '—'}</TableCell>
                        <TableCell className="text-muted-foreground">{item.to_email || '—'}</TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Input
                              value={draft.subject}
                              onChange={(e) => setDraft((prev) => ({ ...prev, subject: e.target.value }))}
                              data-testid={`edit-subject-${item.id}`}
                            />
                          ) : (
                            item.subject || '—'
                          )}
                        </TableCell>
                        <TableCell className="max-w-md">
                          {isEditing ? (
                            <Textarea
                              value={draft.body}
                              onChange={(e) => setDraft((prev) => ({ ...prev, body: e.target.value }))}
                              rows={4}
                              data-testid={`edit-body-${item.id}`}
                            />
                          ) : (
                            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{item.body_preview || '—'}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{item.touch_number ?? '—'}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={item.status === 'approved' ? 'default' : 'outline'}>
                            {item.status === 'approved' ? 'Approved — awaiting send' : 'Drafted'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {isEditing ? (
                              <>
                                <Button size="sm" onClick={() => saveEdit(item)} disabled={isProcessing} data-testid={`save-${item.id}`}>
                                  {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}
                                </Button>
                                <Button size="sm" variant="outline" onClick={cancelEdit} disabled={isProcessing}>
                                  <X className="w-4 h-4 mr-1" /> Cancel
                                </Button>
                              </>
                            ) : item.status === 'approved' ? (
                              <>
                                <Button size="sm" onClick={() => handleSend(item)} disabled={isProcessing} data-testid={`send-${item.id}`}>
                                  {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Send</>}
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => handleReject(item)} disabled={isProcessing} data-testid={`reject-${item.id}`}>
                                  <XCircle className="w-4 h-4 mr-1" /> Reject
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button size="sm" onClick={() => handleApprove(item)} disabled={isProcessing} data-testid={`approve-${item.id}`}>
                                  {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-1" /> Approve</>}
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => startEdit(item)} disabled={isProcessing} data-testid={`edit-${item.id}`}>
                                  <Pencil className="w-4 h-4 mr-1" /> Edit
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => handleReject(item)} disabled={isProcessing} data-testid={`reject-${item.id}`}>
                                  <XCircle className="w-4 h-4 mr-1" /> Reject
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {rowMessages[item.id] && (
                        <TableRow>
                          <TableCell colSpan={7} className="bg-destructive/10 text-destructive text-sm py-2">
                            <div className="flex items-start gap-2">
                              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                              <span>{rowMessages[item.id]}</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {failedItems.length > 0 && (
        <Card className="bg-card border-border card-glow mt-6 border-destructive/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" /> Failed / Retry ({failedItems.length})
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              These were approved but the send attempt failed. They never left approved
              status, so Retry re-runs the same send.
            </p>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {failedItems.map((item) => (
                  <TableRow key={item.id} data-testid={`failed-row-${item.id}`}>
                    <TableCell className="font-medium">{item.firm_name || '—'}</TableCell>
                    <TableCell className="text-muted-foreground">{item.to_email || '—'}</TableCell>
                    <TableCell>{item.subject || '—'}</TableCell>
                    <TableCell className="text-sm text-destructive">{item.reason || '—'}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        onClick={() => handleRetry(item)}
                        disabled={!!processing[`retry-${item.id}`]}
                        data-testid={`retry-${item.id}`}
                      >
                        {processing[`retry-${item.id}`] ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <><RotateCw className="w-4 h-4 mr-1" /> Retry</>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {heldItems.length > 0 && (
        <Card className="bg-card border-border card-glow mt-6 border-amber-500/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" /> Held for review ({heldItems.length})
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Real prospects, sourced but not yet reviewed. Invisible to Approve &amp; Send All
              until you release each one individually.
            </p>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {heldItems.map((item) => (
                  <TableRow key={item.id} data-testid={`held-row-${item.id}`}>
                    <TableCell className="font-medium">{item.firm_name || '—'}</TableCell>
                    <TableCell className="text-muted-foreground">{item.to_email || '—'}</TableCell>
                    <TableCell>{item.subject || '—'}</TableCell>
                    <TableCell className="max-w-md">
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{item.body_preview || '—'}</p>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        onClick={() => handleRelease(item)}
                        disabled={!!processing[`release-${item.id}`]}
                        data-testid={`release-${item.id}`}
                      >
                        {processing[`release-${item.id}`] ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <><Unlock className="w-4 h-4 mr-1" /> Release to queue</>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OutreachQueue;
